// src/components/parent/JobSeekerSearch.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { searchJobSeekers } from '../../services/firestore';
import { FaEnvelope, FaStar, FaRegStar, FaUser, FaCheckCircle } from 'react-icons/fa';

const JobSeekerSearch = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  // Configuration data
  const config = {
    types: [
      { value: '', label: 'All Types' },
      { value: 'nanny', label: 'Nanny' },
      { value: 'educator', label: 'Early Childhood Educator' }
    ],
    certifications: [
      'First Aid', 'CPR', 'Montessori', 'Early Childhood Education'
    ],
    languages: [
      'English', 'Spanish', 'French', 'Mandarin', 'Hindi', 'Punjabi'
    ]
  };

  // State
  const [filters, setFilters] = useState({
    type: '',
    criminalRecordCheck: '',
    certifications: [],
    languages: []
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [selectedJobSeeker, setSelectedJobSeeker] = useState(null);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Handle checkbox filter changes
  const handleCheckboxFilterChange = (e, field) => {
    const { value, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value] 
        : prev[field].filter(item => item !== value)
    }));
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      type: '',
      criminalRecordCheck: '',
      certifications: [],
      languages: []
    });
  };

  // Perform search
  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    try {
      const { jobSeekers } = await searchJobSeekers(filters);
      setResults(jobSeekers || []);
      setSearchPerformed(true);
      setSelectedJobSeeker(null);
    } catch (err) {
      setError('Error performing search: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Format list for display
  const formatList = list => !list || list.length === 0 ? 'N/A' : list.join(', ');

  // Handle jobseeker card click
  const handleJobSeekerClick = (jobSeeker) => {
    setSelectedJobSeeker(jobSeeker);
  };

  // Handle View Profile button click
  const handleViewProfile = (jobSeekerId) => {
    navigate(`/jobseeker/${jobSeekerId}`);
  };

  // Handle Contact button click
  const handleContact = (jobSeekerId) => {
    navigate(`/messages`, { 
      state: { initiateChat: true, receiverId: jobSeekerId }
    });
  };

  return (
    <div className="jobseeker-search">
      <h1>Find Caregivers</h1>
      {error && <div className="error-message">{error}</div>}
      <div className="search-container">
        <div className="filters-container">
          <h2>Search Filters</h2>
          <div className="form-section">
            <h3>Basic Filters</h3>
            <div className="form-group">
              <label htmlFor="type">Caregiver Type:</label>
              <select id="type" name="type" value={filters.type} onChange={handleFilterChange}>
                {config.types.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="criminalRecordCheck">Criminal Record Check:</label>
              <select id="criminalRecordCheck" name="criminalRecordCheck" value={filters.criminalRecordCheck} onChange={handleFilterChange}>
                <option value="">Any</option>
                <option value="true">Completed</option>
                <option value="false">Not Completed</option>
              </select>
            </div>
            <div className="form-group">
              <label>Certifications:</label>
              <div className="checkbox-grid">
                {config.certifications.map(cert => (
                  <div className="checkbox-item" key={cert}>
                    <input
                      type="checkbox"
                      id={`cert-${cert}`}
                      value={cert}
                      checked={filters.certifications.includes(cert)}
                      onChange={e => handleCheckboxFilterChange(e, 'certifications')}
                    />
                    <label htmlFor={`cert-${cert}`}>{cert}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Languages:</label>
              <div className="checkbox-grid">
                {config.languages.map(lang => (
                  <div className="checkbox-item" key={lang}>
                    <input
                      type="checkbox"
                      id={`lang-${lang}`}
                      value={lang}
                      checked={filters.languages.includes(lang)}
                      onChange={e => handleCheckboxFilterChange(e, 'languages')}
                    />
                    <label htmlFor={`lang-${lang}`}>{lang}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="search-actions">
            <button 
              type="button"
              className="reset-button" 
              onClick={handleResetFilters} 
              disabled={loading}
            >
              Reset Filters
            </button>
            <button 
              type="button"
              className="search-button" 
              onClick={handleSearch} 
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search Caregivers'}
            </button>
          </div>
        </div>
        {/* Search Results */}
        <div className="search-results-container">
          <div className="results-header">
            <h2>Search Results</h2>
            {results.length > 0 && (
              <div className="results-stats">
                <p className="results-count">Found {results.length} caregivers</p>
              </div>
            )}
          </div>
          {loading ? (
            <div className="loading-container">
              <p>Searching for caregivers...</p>
            </div>
          ) : searchPerformed && results.length === 0 ? (
            <div className="no-results">
              <h3>No caregivers found matching your criteria</h3>
              <p>Try adjusting your filters or search in a different area.</p>
            </div>
          ) : searchPerformed && (
            <div className="results-content">
              <div className="provider-list">
                {results.map((jobSeeker) => (
                  <div className="provider-card" key={jobSeeker.id} onClick={() => handleJobSeekerClick(jobSeeker)}>
                    <div className="provider-card-header">
                      <h3>{jobSeeker.displayName || jobSeeker.profile?.displayName || 'Caregiver'}</h3>
                      {jobSeeker.profile?.criminalRecordCheck && (
                        <div className="licensed-badge">
                          <FaCheckCircle size={14} className="icon-margin-right" />
                          Criminal Record Check
                        </div>
                      )}
                    </div>
                    <div className="provider-card-body">
                      <div className="jobseeker-type">
                        {jobSeeker.profile?.type ? jobSeeker.profile.type.charAt(0).toUpperCase() + jobSeeker.profile.type.slice(1) : 'N/A'}
                      </div>
                      <div className="jobseeker-certifications">
                        <strong>Certifications:</strong> {formatList(jobSeeker.profile?.certifications)}
                      </div>
                      <div className="jobseeker-languages">
                        <strong>Languages:</strong> {formatList(jobSeeker.profile?.languages)}
                      </div>
                      <div className="provider-rating">
                        <div className="stars">
                          {[...Array(5)].map((_, i) => {
                            const rating = jobSeeker.averageRating || 0;
                            return i < Math.floor(rating) ? (
                              <FaStar key={i} className="star-filled" />
                            ) : (
                              <FaRegStar key={i} className="star-empty" />
                            );
                          })}
                        </div>
                        <span className="reviews-count">
                          ({jobSeeker.reviewCount || 0} reviews)
                        </span>
                      </div>
                    </div>
                    <div className="provider-card-footer">
                      <button 
                        className="view-profile-button"
                        onClick={e => {
                          e.stopPropagation();
                          handleViewProfile(jobSeeker.id);
                        }}
                      >
                        View Full Profile
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {/* Selected JobSeeker Panel */}
              {selectedJobSeeker && (
                <div className="selected-provider-panel">
                  <div className="panel-header">
                    <h3>{selectedJobSeeker.displayName || selectedJobSeeker.profile?.displayName || 'Caregiver'}</h3>
                    <button className="close-panel-button" onClick={() => setSelectedJobSeeker(null)}>×</button>
                  </div>
                  <div className="panel-content">
                    <div className="provider-details">
                      <div><strong>Type:</strong> {selectedJobSeeker.profile?.type || 'N/A'}</div>
                      <div><strong>Certifications:</strong> {formatList(selectedJobSeeker.profile?.certifications)}</div>
                      <div><strong>Languages:</strong> {formatList(selectedJobSeeker.profile?.languages)}</div>
                      <div><strong>Bio:</strong> {selectedJobSeeker.profile?.bio || 'N/A'}</div>
                    </div>
                    <div className="provider-actions">
                      <button className="view-profile-button" onClick={() => handleViewProfile(selectedJobSeeker.id)}>
                        View Full Profile
                      </button>
                      <button className="contact-button" onClick={() => handleContact(selectedJobSeeker.id)}>
                        Contact Caregiver
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobSeekerSearch;